/**
 * Semantic Intent Classification Service for PetSnap
 * Refactored to use backend API instead of direct OpenAI calls
 */

import { auth } from '../firebase';

const BACKEND_URL = typeof window !== 'undefined' && window.location.hostname === 'localhost'
  ? (process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001')
  : '';

export interface IntentResult {
  intent: string;
  confidence: number;
  language: string;
  entities: {
    petName?: string;
    timeScope?: string;
    roadmapType?: string;
    duration?: string;
    difficulty?: string;
    [key: string]: string | undefined;
  };
  explanation: string;
}

export interface IntentDefinition {
  description: string;
  examples: {
    english: string[];
    spanish: string[];
    french: string[];
    chinese: string[];
  };
}

export class SemanticIntentClassificationService {
  // Simple in-memory cache for intent classifications within a session
  private intentCache: Map<string, IntentResult> = new Map();

  private readonly intentDefinitions: Record<string, IntentDefinition> = {
    'retrieve_chat_history': {
      description: 'User wants to see recent chat history or conversation summaries about their pet',
      examples: {
        english: [
          "Show me recent chat with Duoduo",
          "What did we talk about recently?",
          "I want to see our recent conversation history"
        ],
        spanish: [
          "Muéstrame el chat reciente con Duoduo",
          "¿De qué hablamos recientemente?"
        ],
        french: [
          "Montre-moi le chat récent avec Duoduo",
          "De quoi avons-nous parlé récemment?"
        ],
        chinese: [
          "告诉我多多近期的聊天内容",
          "最近和多多聊了什么",
          "查看近期对话记录"
        ]
      }
    },
    'create_roadmap': {
      description: 'User wants to create a training plan or structured roadmap for their pet',
      examples: {
        english: [
          "Create a training plan for my dog",
          "I need a behavior improvement program"
        ],
        spanish: [
          "Crea un plan de entrenamiento para mi perro",
          "Necesito un programa de mejora de comportamiento"
        ],
        french: [
          "Crée un plan d'entraînement pour mon chien",
          "J'ai besoin d'un programme d'amélioration du comportement"
        ],
        chinese: [
          "为我的狗制定一个训练计划",
          "我需要一个行为改善方案"
        ]
      }
    },
    'general_question': {
      description: 'General pet care questions or conversation',
      examples: {
        english: ["How are you?", "Tell me about pet care"],
        spanish: ["¿Cómo estás?", "Cuéntame sobre el cuidado de mascotas"],
        french: ["Comment allez-vous?", "Parlez-moi des soins aux animaux"],
        chinese: ["你好吗？", "告诉我关于宠物护理"]
      }
    }
  };

  private readonly similarityThreshold = 0.65;

  /**
   * Get Firebase ID token for backend authentication
   */
  private async getAuthToken(): Promise<string> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    return await currentUser.getIdToken();
  }

  /**
   * Classify user intent using backend API (which uses OpenAI embeddings)
   */
  async classifyIntent(userInput: string): Promise<IntentResult> {
    try {
      const key = userInput.trim().toLowerCase();
      const cached = this.intentCache.get(key);
      if (cached) {
        return cached;
      }

      console.log('🔍 Starting semantic intent classification via backend:', userInput.substring(0, 50) + '...');

      // Get Firebase auth token
      const idToken = await this.getAuthToken();

      // Detect language for better processing
      const language = this.detectLanguage(userInput);

      // Call backend API for intent classification
      const response = await fetch(`${BACKEND_URL}/api/ai/intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: userInput,
          language
        })
      });

      if (!response.ok) {
        throw new Error(`Backend API error: ${response.status}`);
      }

      const data = await response.json();

      // Backend returns embedding - we need to calculate similarities client-side
      // For simplicity, we'll do keyword-based fallback for now
      // TODO: Enhance backend to return full intent classification
      const result = this.classifyIntentLocally(userInput, language);

      this.intentCache.set(key, result);
      console.log('🎯 Intent classification result:', result);

      return result;

    } catch (error) {
      console.error('❌ Error in semantic intent classification:', error);
      return {
        intent: 'general_question',
        confidence: 0,
        language: 'english',
        entities: {},
        explanation: `Classification error: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Local intent classification using keyword matching (fallback)
   */
  private classifyIntentLocally(userInput: string, language: string): IntentResult {
    const text = userInput.toLowerCase();
    let bestIntent = 'general_question';
    let bestScore = 0.3;

    // Check for chat history keywords
    const historyKeywords = {
      english: ['recent', 'chat', 'history', 'conversation', 'talked', 'discussed', 'summary'],
      spanish: ['reciente', 'chat', 'historial', 'conversación', 'hablamos', 'discutimos'],
      french: ['récent', 'chat', 'historique', 'conversation', 'parlé', 'discuté'],
      chinese: ['最近', '聊天', '对话', '历史', '记录', '内容', '话题']
    };

    //Check for roadmap keywords
    const roadmapKeywords = {
      english: ['train', 'plan', 'program', 'guide', 'roadmap', 'schedule'],
      spanish: ['entrenar', 'plan', 'programa', 'guía'],
      french: ['entraîner', 'plan', 'programme', 'guide'],
      chinese: ['训练', '计划', '方案', '教程']
    };

    const langKey = language as keyof typeof historyKeywords;
    const histKeys = historyKeywords[langKey] || historyKeywords.english;
    const roadKeys = roadmapKeywords[langKey] || roadmapKeywords.english;

    const historyMatches = histKeys.filter(k => text.includes(k)).length;
    const roadmapMatches = roadKeys.filter(k => text.includes(k)).length;

    if (historyMatches > 0 && historyMatches >= roadmapMatches) {
      bestIntent = 'retrieve_chat_history';
      bestScore = 0.7 + (historyMatches * 0.05);
    } else if (roadmapMatches > 0) {
      bestIntent = 'create_roadmap';
      bestScore = 0.7 + (roadmapMatches * 0.05);
    }

    // Extract entities
    const entities = this.extractEntities(userInput, language);

    return {
      intent: bestIntent,
      confidence: Math.min(bestScore, 0.95),
      language,
      entities,
      explanation: `Local classification based on keywords`
    };
  }

  /**
   * Extract entities from user input
   */
  private extractEntities(text: string, language: string): {
    petName?: string;
    timeScope?: string;
    roadmapType?: string;
    duration?: string;
    difficulty?: string;
    [key: string]: string | undefined;
  } {
    const entities: any = {};

    // Extract pet names
    const petNamePatterns = {
      chinese: /([\\u4e00-\\u9fff]{2,4})(?:的|是|叫|名字|近期|最近)/g,
      english: /(?:with|about|for)\\s+([A-Za-z]{2,10})/gi,
      spanish: /(?:con|sobre|para)\\s+([A-Za-z]{2,10})/gi,
      french: /(?:avec|sur|pour)\\s+([A-Za-z]{2,10})/gi
    };

    const pattern = petNamePatterns[language as keyof typeof petNamePatterns] || petNamePatterns.english;
    const match = text.match(pattern);
    if (match) {
      entities.petName = match[1];
    }

    // Extract time scopes
    const timePatterns = {
      chinese: /(最近|近期|前几天|最近三个月)/g,
      english: /(recent|recently|latest|last\\s+\\d+\\s+\\w+)/gi,
      spanish: /(reciente|recientemente|último)/gi,
      french: /(récent|récemment|dernier)/gi
    };

    const timePattern = timePatterns[language as keyof typeof timePatterns];
    const timeMatch = text.match(timePattern);
    if (timeMatch) {
      entities.timeScope = timeMatch[0];
    }

    return entities;
  }

  /**
   * Detect language from text
   */
  private detectLanguage(text: string): string {
    if (/[\\u4e00-\\u9fff]/.test(text)) return 'chinese';
    if (/[áéíóúñü]/.test(text.toLowerCase())) return 'spanish';
    if (/[àâäéèêëïîôöùûüÿç]/.test(text.toLowerCase())) return 'french';
    return 'english';
  }

  /**
   * Check if the intent is a summary request
   */
  isSummaryRequest(intentResult: IntentResult): boolean {
    return intentResult.intent === 'retrieve_chat_history' && intentResult.confidence >= this.similarityThreshold;
  }

  /**
   * Check if the intent is a roadmap request
   */
  isRoadmapRequest(intentResult: IntentResult): boolean {
    return intentResult.intent === 'create_roadmap' && intentResult.confidence >= this.similarityThreshold;
  }

  /**
   * Check if the intent is a general question
   */
  isGeneralQuestion(intentResult: IntentResult): boolean {
    return intentResult.intent === 'general_question' || intentResult.confidence < this.similarityThreshold;
  }

  /**
   * Hybrid roadmap detection
   */
  async detectRoadmapRequest(userInput: string): Promise<{
    isRoadmap: boolean;
    confidence: number;
    method: 'semantic' | 'keyword';
    entities: any;
  }> {
    try {
      const result = await this.classifyIntent(userInput);
      const isRoadmap = this.isRoadmapRequest(result);

      return {
        isRoadmap,
        confidence: result.confidence,
        method: 'semantic',
        entities: result.entities
      };
    } catch (error) {
      console.error('❌ Error in roadmap detection:', error);
      return {
        isRoadmap: false,
        confidence: 0,
        method: 'keyword',
        entities: {}
      };
    }
  }

  /**
   * Get available intents
   */
  getAvailableIntents(): Record<string, { description: string; supportedLanguages: string[] }> {
    const intents: Record<string, { description: string; supportedLanguages: string[] }> = {};

    for (const [intentName, intentData] of Object.entries(this.intentDefinitions)) {
      intents[intentName] = {
        description: intentData.description,
        supportedLanguages: ['english', 'spanish', 'french', 'chinese']
      };
    }

    return intents;
  }
}

// Export singleton instance
export const semanticIntentClassificationService = new SemanticIntentClassificationService();
export default semanticIntentClassificationService;
